/**
 * Les **neuf routes `/api/*`** de SPEC-API §5, typées et appelées depuis un seul endroit.
 *
 * | § | Route | Ce qu'elle rend |
 * |---|---|---|
 * | 5.1 | `GET /api/me` | le compte + l'index de ses runs (mémoriaux compris) |
 * | 5.2 | `POST /api/runs` | la run créée, `turn: 0` |
 * | 5.3 | `GET /api/runs/active` | les runs jouables (0, 1 ou 2 — au plus une par mode) |
 * | 5.5 | `POST /api/runs/:id/actions` | **l'unique écriture de gameplay** |
 * | 5.6 | `GET /api/runs/:id` | le détail, et le mémorial d'une run finie |
 * | 5.7 | `GET /api/runs/:id/atelier-pattern` | les 5 patterns seedés du tour courant |
 * | 5.8 | `GET /api/leaderboard` | le classement |
 * | 5.9 | `PATCH /api/runs/:id/settings` | la tolérance d'accessibilité |
 * | 5.10 | `GET /api/content` | le bundle de contenu servi par le serveur |
 *
 * **Ce module ne décide de rien** : il nomme, il type, il appelle. Toute la logique de reprise,
 * d'idempotence et de traduction du vocabulaire vit dans `game/serverGameClient.ts`.
 *
 * ⚠️ **Les types ci-dessous décrivent ce que le serveur envoie AUJOURD'HUI**, pas seulement ce
 * que §5 littéral décrit : `routes/runs.ts` et `routes/actions.ts` portent plusieurs champs
 * additifs, chacun signalé dans leur code (`channel_name` ⟦A41⟧, `progression`, `victory`,
 * `memorial.cause`). Les décrire fidèlement ici, c'est ce qui permet au client de les utiliser
 * sans les deviner.
 */

import type { EngineEvent, GameState, LogEntry, RecapDelegue, Recapitulatif, RunMode } from "@maxime/shared";

import { appelApi } from "./http";
import type { Utilisateur } from "./auth";

// ─────────────────────────────────────────────────────────────────────────────
// Formes de réponse
// ─────────────────────────────────────────────────────────────────────────────

/**
 * ⟦A-97 §1.1⟧ Le résumé chiffré d'une run — **servi par `GET /api/me` et par lui seul**.
 *
 * ⚠️ `subs` n'est pas « les abonnés au sommet » : aucun pic n'est stocké côté serveur (ni
 * colonne de `runs`, ni champ de `GameState`). C'est le compte à l'instant lu — final pour une
 * run terminée, courant pour une run vivante. Le nom dit ce que le chiffre est.
 */
export interface ResumeRunApi {
  subs: number;
  /** La durée **simulée** (jours de jeu), jamais le temps réel écoulé depuis la création. */
  days_simulated: number;
  videos_count: number;
}

/** La projection `runPublic` du serveur — **jamais** le `seed`, qui est le secret de la run. */
export interface RunApi {
  id: string;
  channel_name: string;
  mode: RunMode;
  status: "active" | "won_continuing" | "ended";
  ended_reason: string | null;
  victory_ending: string | null;
  victory_turn: number | null;
  victory_score: number | null;
  content_version: string;
  created_at: number;
  updated_at: number;
  /**
   * ⟦A-97 §1.1⟧ **Optionnel, et il DOIT le rester** : seul l'index de `GET /api/me` le calcule.
   * Les quatre autres routes qui publient un bloc `run` (`POST /runs`, `/runs/active`,
   * `/runs/:id`, l'abandon) ne le portent pas — le typer obligatoire ferait mentir le type sur
   * quatre réponses.
   */
  resume?: ResumeRunApi;
}

/**
 * L'état **public** (§5.4) : le `GameState` du moteur moins ses secrets.
 *
 * ⚠️ **Ce n'est pas tout à fait un `GameState`, et le typer comme tel est un choix assumé.**
 * Deux champs sont amputés côté serveur : `trends[].phase` / `phase_elapsed_h` (absents sans la
 * capacité *Analytics addict*) et `inspiration[].potentiel_evergreen` (absent sans *SEO*). Ce
 * sont exactement les deux endroits où l'UI teste déjà la capacité avant d'afficher
 * (`Studio.tsx`, `NouvelleVideo.tsx`) — le masquage serveur et l'affichage client sont donc
 * d'accord. Un champ absent y est lu comme `undefined`, ce qui donne « tiède » plutôt qu'une
 * fuite : **le sens par défaut est le sens sûr**.
 */
export type EtatServeur = GameState;

/** Une ligne `videos` telle que §5.3/§5.6 la publient (snake_case, id `"{run_id}:{n}"`). */
export interface VideoApi {
  id: string;
  turn_published: number;
  kind: "short" | "long";
  subject_id: string;
  title: string;
  class: string;
  alignment: number;
  execution_score: number;
  peak_views: number;
  total_views: number;
  weekly_rent: number;
  status: string;
  visibility: "public" | "private";
  series_id: string | null;
  milestone_id: string | null;
  created_at: number;
}

/** §7.3 — la chute en mode normal, telle que le serveur la raconte. */
export interface RetourAuJalonApi {
  palier: number | null;
  cause: string;
  subs_avant: number;
  subs_apres: number;
  argent_avant: number;
  argent_apres: number;
  reputation_avant: number;
  jour_avant: number;
  jour_apres: number;
}

/**
 * ⟦A-74⟧ Une ligne du score de victoire décomposé — la forme EXACTE de
 * `server/src/progression.ts` (`LigneScore`) : « de quoi refaire le calcul à la main, pas
 * seulement le résultat ». Le type était `Record<string, number>` : faux, et il jetait tout
 * ce que le serveur envoie — c'est lui qui rendait l'écran de score inécrivable.
 */
export interface LigneScoreApi {
  id: "vitesse" | "fidelite" | "jalons" | "bad_buzz" | "argent";
  points: number;
  max: number;
  ratio: number;
  valeur: number;
  reference: number;
}

export interface DecompositionScoreApi {
  total: number;
  total_max: number;
  lignes: LigneScoreApi[];
}

/** Le bloc `progression` de §5.5 (champ additif signalé côté serveur). */
export interface ProgressionApi {
  paliers_franchis: number[];
  retour_au_jalon: RetourAuJalonApi | null;
  fin_verrouillee: string | null;
  victoire: {
    ending: string | null;
    score: number | null;
    turn: number;
    /** ⟦A-74⟧ Les cinq lignes de 200 points (PLAN §4.13, ⟦A-14 §3⟧). */
    decomposition?: DecompositionScoreApi | null;
  } | null;
}

/** La réponse `200` de `POST /api/runs/:id/actions`. */
export interface ReponseAction {
  turn: number;
  state: EtatServeur;
  events: EngineEvent[];
  /**
   * La trame écrite par le **moteur** (`{ turn, clock_h, text }`) — champ additif à §5.5,
   * signalé côté serveur. Le journal la reprend telle quelle au lieu de la refabriquer depuis
   * les `events` : un récit écrit deux fois est un récit qui divergera.
   */
  log: LogEntry[];
  /** ⟦A-16⟧ Le récapitulatif des jours écoulés, ou `null` quand la résolution n'en porte pas. */
  recap: Recapitulatif | null;
  /**
   * ⟦SPEC-PRODUCTEUR §5.5 / A-66⟧ Le récapitulatif de **délégation** — `null` sauf après
   * `laisser_tourner`. Champ additif à §5.5, signalé côté serveur.
   */
  delegation: RecapDelegue | null;
  run: RunApi;
  progression: ProgressionApi;
  /** Présent **uniquement** si l'action a publié une vidéo (§5.5). */
  video?: VideoApi;
}

export interface RunActive {
  run: RunApi;
  turn: number;
  state: EtatServeur;
  skills: string[];
  videos: VideoApi[];
}

export interface ReponseDetailRun extends RunActive {
  memorial: {
    epitaph: string;
    subs_final: number;
    videos_count: number;
    days_simulated: number;
    cause: string | null;
    screen: "internet_ta_oublie" | "victoire";
  } | null;
  victory: unknown;
}

export interface EntreeClassement {
  /**
   * Le rang, **calculé par le serveur** (`routes/leaderboard.ts` : `i + 1` après tri). Champ
   * additif à §5.8, décrit ici parce qu'il arrive vraiment — et parce que le recalculer côté
   * client à partir de l'index du tableau marcherait par accident : ça ne marcherait plus le
   * jour où `limit` ou un filtre ferait commencer la page ailleurs qu'au premier.
   */
  rank: number;
  name: string;
  mode: RunMode;
  ending: string | null;
  score_total: number;
  subs_final: number;
  days_simulated: number;
  created_at: number;
}

/** L'enveloppe d'action de §5.5. `action_id` est généré **avant** l'envoi (§7.2). */
export interface EnveloppeAction {
  expected_turn: number;
  action_id: string;
  action: Record<string, unknown>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Appels
// ─────────────────────────────────────────────────────────────────────────────

/** ⟦A-8⟧ Ce que l'écran de publication révèle — la préférence du JOUEUR, persistée côté compte. */
export type ModeRevelationApi = "reel" | "arcade";

export interface PreferencesApi {
  reveal_mode: ModeRevelationApi;
  /** ⟦C8 · A-120⟧ Le minuteur du manager, opt-in — défaut `false`. */
  manager_delai_actif: boolean;
}

/**
 * §5.1 — le compte, l'index de **toutes** ses runs (c'est par là que passe le mémorial), et
 * ⟦A-8 / M2.40 · A-81⟧ ses **préférences** (défaut `reel` quand le compte n'a jamais rien réglé).
 */
export function me(): Promise<{ user: Utilisateur; runs: RunApi[]; preferences: PreferencesApi }> {
  return appelApi("/me");
}

/**
 * ⟦A-8 / M2.40 · A-81⟧ `PATCH /api/me/preferences` — route additive à §5, signalée côté serveur.
 * Ce n'est pas une action de jeu : aucun `turn`, aucune run, le moteur n'en sait rien.
 *
 * ⟦C8 · A-120⟧ Les deux champs sont optionnels côté serveur : un `Partial` règle l'un OU
 * l'autre sans réécraser celui qu'on ne touche pas.
 */
export function reglerPreferences(
  corps: Partial<PreferencesApi>,
): Promise<{ preferences: PreferencesApi }> {
  return appelApi("/me/preferences", { methode: "PATCH", corps });
}

/**
 * §5.2 — créer une run.
 *
 * `channel_name` et `genre` sont des champs **additifs** au littéral de §5.2, tous deux
 * signalés côté serveur : le premier vient de ⟦A41⟧ (colonne `NOT NULL`, épitaphe et
 * classement le lisent), le second d'⟦A-4 §2⟧ (le genre est devenu mécanique). Le client les
 * envoie parce que le serveur les attend.
 *
 * ⚠️ **La graine n'est pas un paramètre.** `POST /api/runs` la tire lui-même (16 octets) et ne
 * la renvoie jamais — c'est le secret qui rend les événements et les patterns imprévisibles.
 * Rejouer une graine choisie reste donc une possibilité du **bac à sable uniquement**.
 */
export function creerRun(corps: {
  mode: RunMode;
  channel_name: string;
  assist_tolerance?: boolean;
  genre?: "f" | "h";
}): Promise<{ run: RunApi; turn: number; state: EtatServeur }> {
  return appelApi("/runs", { methode: "POST", corps });
}

/** §5.3 — restitution au chargement. Jamais de `404` : liste vide si aucune run. */
export function runsActives(): Promise<{ runs: RunActive[] }> {
  return appelApi("/runs/active");
}

/** §5.5 — l'unique écriture de gameplay. */
export function posterAction(runId: string, enveloppe: EnveloppeAction): Promise<ReponseAction> {
  return appelApi(`/runs/${encodeURIComponent(runId)}/actions`, {
    methode: "POST",
    corps: enveloppe,
  });
}

/**
 * **Route additive** (signalée côté serveur) — abandonner une run pour de bon.
 *
 * Abandonner n'est pas supprimer : la run passe en `ended` / `abandoned` et reste entière
 * derrière `GET /api/runs/:id`, mémorial compris (PLAN §4.11). Sans cet appel, une run
 * abandonnée n'était oubliée que **localement** — `/runs/active` la remettait dans les mains du
 * joueur au rechargement, et `POST /runs` refusait la suivante en `409 active_run_exists`.
 */
export function abandonnerRun(runId: string): Promise<{ run: RunApi }> {
  return appelApi(`/runs/${encodeURIComponent(runId)}/abandon`, { methode: "POST" });
}

/** §5.6 — détail et mémorial. Accessible quel que soit le statut de la run. */
export function detailRun(runId: string): Promise<ReponseDetailRun> {
  return appelApi(`/runs/${encodeURIComponent(runId)}`);
}

/**
 * §5.7 — les 5 patterns seedés du tour courant : voir **`api/atelier.ts`**.
 *
 * Cet appel vit à côté des timelines qu'il commande plutôt qu'ici : le pattern descendant et la
 * timeline montante sont **un seul contrat**, celui de `server/src/atelier/`, et les séparer
 * était ce qui rendait facile de décrire l'un sans l'autre. Ré-exporté pour que les neuf routes
 * de §5 restent atteignables depuis ce module.
 */
export { patternsAtelier } from "./atelier";

/** §5.8 — le classement. */
export function classement(params: { mode?: RunMode; limit?: number } = {}): Promise<{
  entries: EntreeClassement[];
}> {
  const q = new URLSearchParams();
  if (params.mode) q.set("mode", params.mode);
  if (params.limit !== undefined) q.set("limit", String(params.limit));
  const requete = q.toString();
  return appelApi(`/leaderboard${requete.length > 0 ? `?${requete}` : ""}`);
}

/** §5.9 — la tolérance d'accessibilité, côté run (fenêtres +30 %, score plafonné à 85). */
export function reglerRun(
  runId: string,
  corps: { assist_tolerance: boolean },
): Promise<{ settings: { assist_tolerance: boolean } }> {
  return appelApi(`/runs/${encodeURIComponent(runId)}/settings`, { methode: "PATCH", corps });
}

/**
 * §5.10 — le bundle de contenu servi par le serveur.
 *
 * Non branché pour l'instant : `game/content.ts` importe les `content/*.json` au build et les
 * valide avec les mêmes schémas Zod que le serveur, ce qui donne le **même** contenu tant que
 * le client et le serveur sont déployés ensemble (ils le sont : un seul conteneur). Le jour où
 * ce ne sera plus vrai, `content_version` de la run le dira, et c'est cet appel qui prendra le
 * relais.
 */
export function contenuServeur(): Promise<{ content_version: string } & Record<string, unknown>> {
  return appelApi("/content");
}
